import * as signalR from "@microsoft/signalr";

let connection = null;

/**
 * Start a SignalR connection to the Azure SignalR hub.
 * @param {number|string} userId   - The logged-in user's ID (used in negotiate header)
 * @param {Function} onNewNotification - Callback fired when a "newNotification" message arrives
 * @param {string} authToken - The auth token for API authentication (optional; will fallback to localStorage/sessionStorage)
 */
export async function startSignalRConnection(
  userId,
  onNewNotification,
  authToken,
) {
  const negotiateUrl = process.env.REACT_APP_SIGNALR_NEGOTIATE;
  if (!negotiateUrl) {
    console.warn(
      "[SignalR Trace] REACT_APP_SIGNALR_NEGOTIATE not set — SignalR disabled.",
    );
    return;
  }

  console.log("[SignalR Trace] Using negotiateUrl:", negotiateUrl);

  // Already connected — no-op
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    console.log("[SignalR Trace] Connection already exists and is connected.");
    return;
  }

  console.log("[SignalR Trace] Building HubConnection...");

  // Clean URL: Remove trailing '/negotiate' if present (SignalR client adds it automatically)
  const cleanedUrl = negotiateUrl.replace(/\/negotiate\/?$/, "");

  let token =
    authToken ||
    localStorage.getItem("authToken") ||
    sessionStorage.getItem("authToken") ||
    "";

  if (!token) {
    console.error(
      "[SignalR Trace] No auth token provided or found in localStorage/sessionStorage. SignalR connection aborted.",
    );
    return;
  }

  const urlWithUserId = `${cleanedUrl}${cleanedUrl.includes("?") ? "&" : "?"}userId=${userId.toString()}`;

  console.log("[SignalR Trace] Hub URL:", urlWithUserId);

  connection = new signalR.HubConnectionBuilder()
    .withUrl(urlWithUserId, {
      accessTokenFactory: () => token, // Automatically adds Authorization: Bearer header
      skipNegotiation: false,
      transport:
        signalR.HttpTransportType.WebSockets |
        signalR.HttpTransportType.LongPolling,
    })
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  connection.onreconnected((connectionId) => {
    console.log("[SignalR] Reconnected:", connectionId);
  });

  connection.onclose((err) => {
    console.log("[SignalR] Connection closed:", err);
  });

  connection.on("newNotification", (payload) => {
    console.log("[SignalR Trace] Received newNotification:", payload);
    if (onNewNotification) onNewNotification(payload);
  });

  try {
    console.log("[SignalR Trace] Starting connection...");
    await connection.start();
    console.log("[SignalR Trace] Connected! userId:", userId);
  } catch (err) {
    console.error("[SignalR Trace] Connection failed:", err);
  }
}

/**
 * Stop and cleanup the SignalR connection.
 */
export async function stopSignalRConnection() {
  if (connection) {
    try {
      await connection.stop();
      console.log("[SignalR] Disconnected.");
    } catch (err) {
      console.error("[SignalR] Disconnect error:", err);
    }
    connection = null;
  }
}
